/**
 * Inline Annotations Module
 *
 * Provides inline text annotation functionality for the modern results display.
 * Highlights issues directly in the message text with interactive popovers/bottom sheets.
 *
 * ## Platform Support
 *
 * | Platform | Implementation |
 * |----------|----------------|
 * | Web      | Hover popovers (200ms delay) |
 * | iOS      | Touch bottom sheet with swipe |
 * | Android  | Touch bottom sheet with swipe |
 *
 * Metro bundler automatically resolves platform-specific files.
 *
 * @example
 * import { InlineMessageDisplay } from './inline-annotations';
 *
 * <InlineMessageDisplay
 *   message={analysis.original_text}
 *   issues={analysis.issues}
 *   rewrites={analysis.rewrites}
 *   tier={userTier}
 * />
 */

// Main component
export { InlineMessageDisplay } from './InlineMessageDisplay';
export type { InlineMessageDisplayProps } from './InlineMessageDisplay';

// Tier-aware wrapper
export { GatedInlineAnnotations } from './GatedInlineAnnotations';
export type { GatedInlineAnnotationsProps } from './GatedInlineAnnotations';

// Sub-components
export { IssueHighlight } from './IssueHighlight';
export { IssuePopover } from './IssuePopover';
export { IssueBottomSheet } from './IssueBottomSheet';
export { SegmentedControl } from './SegmentedControl';
export { ProgressIndicator } from './ProgressIndicator';
export { CopyRevisedBar } from './CopyRevisedBar';
export { ScoreDeltaIndicator } from './ScoreDeltaIndicator';

// Hooks
export { useIssueInteraction } from './hooks/useIssueInteraction';
export { useRevisedText } from './hooks/useRevisedText';
export { useRevisionTracking } from './hooks/useRevisionTracking';
export { useCategoryCrossing } from './hooks/useCategoryCrossing';

// Celebration
export { CategoryCelebration } from './CategoryCelebration';

// Progress Ring
export { SessionProgressRing } from './SessionProgressRing';

// Utilities
export {
  highlightText,
  getRevisedText,
  hasChanges,
  countByStatus,
} from './utils/highlightText';
export { mergeIssuesWithRewrites } from './utils/mergeIssuesWithRewrites';

// Types
export type {
  InlineIssue,
  IssueStatus,
  ViewMode,
  TextSegment,
  IssueHighlightProps,
  IssuePopoverProps,
  IssueBottomSheetProps,
  SegmentedControlProps,
  CopyRevisedBarProps,
  ProgressIndicatorProps,
  ScoreDeltaIndicatorProps,
  RevisionTrackingConfig,
} from './types';
export { SEVERITY_COLORS, ISSUE_TYPE_TITLES } from './types';

// Styles
export { styles as inlineAnnotationsStyles, getSeverityStyles } from './inlineAnnotationsStyles';
